// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/romanticOffer.ts
// 🔧 PURPOSE: La Fuga romantica in un posto solo: i due pacchetti (prezzi,
//             notti, foto), l'indirizzo della pagina a tutto schermo
//             (pages/RomanticEscape) e le regole delle date che prima
//             stavano dentro la finestra (RomanticWeekModal):
//             - weekend: check-in solo il sabato, mai in luglio e agosto,
//               check-out 2 notti dopo (il lunedì);
//             - settimana: check-in dal 2 gennaio al 15 aprile e dal 15
//               settembre al 15 dicembre, check-out 6 notti dopo.
//
//             L'indirizzo porta il pacchetto e il passo, così il tasto
//             indietro del telefono (e un reload) torna dove si era:
//             /offers/romantic                              → la scelta
//             /offers/romantic?package=weekend              → i dettagli
//             /offers/romantic?package=weekend&step=request → la richiesta
// ============================================

export type RomanticPackage = 'weekend' | 'week';
export type RomanticStep = 'choose' | 'details' | 'request';

/** Nell'ordine in cui la pagina li mostra. */
export const ROMANTIC_PACKAGES: readonly RomanticPackage[] = ['weekend', 'week'];

export const ROMANTIC_PATH = '/offers/romantic';

/**
 * I prezzi, in euro, per due persone. Sono anche nelle frasi
 * romanticWeek.price / romanticWeekend.price e
 * specialOffers.romanticEscape.priceWeekend / priceWeek, e nell'email che
 * parte da api/send-email: cambiandoli, cambiare anche lì.
 */
export const ROMANTIC_PRICES: Readonly<Record<RomanticPackage, number>> = { weekend: 119, week: 380 };

/** Le notti di ogni pacchetto: il check-out è il check-in più queste. */
export const ROMANTIC_NIGHTS: Readonly<Record<RomanticPackage, number>> = { weekend: 2, week: 6 };

/** Due calici sul mare al tramonto (CC0): la card della Home e la testata della pagina. */
export const ROMANTIC_IMAGE = '/images/offers/romantic-sunset.jpg';

const isPackage = (value: string | null): value is RomanticPackage =>
  value === 'weekend' || value === 'week';

/** L'indirizzo di un passo. Senza pacchetto c'è solo la scelta. */
export function romanticPath(pkg?: RomanticPackage | null, step: RomanticStep = 'details'): string {
  if (!pkg || step === 'choose') return ROMANTIC_PATH;
  const params = new URLSearchParams({ package: pkg });
  if (step === 'request') params.set('step', 'request');
  return `${ROMANTIC_PATH}?${params.toString()}`;
}

/** Il passo dall'indirizzo; un pacchetto sconosciuto riporta alla scelta. */
export function parseRomanticSearch(search: string): { pkg: RomanticPackage | null; step: RomanticStep } {
  const params = new URLSearchParams(search);
  const pkg = params.get('package');
  if (!isPackage(pkg)) return { pkg: null, step: 'choose' };
  return { pkg, step: params.get('step') === 'request' ? 'request' : 'details' };
}

/** Il check-out: il check-in più le notti del pacchetto (giorni di calendario). */
export function romanticCheckout(checkIn: Date, pkg: RomanticPackage): Date {
  const out = new Date(checkIn);
  out.setDate(out.getDate() + ROMANTIC_NIGHTS[pkg]);
  return out;
}

/** Settimana: dal 2 gennaio al 15 aprile e dal 15 settembre al 15 dicembre. */
function isWeekCheckIn(date: Date): boolean {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const firstPeriod = (month === 1 && day >= 2) || month === 2 || month === 3 || (month === 4 && day <= 15);
  const secondPeriod = (month === 9 && day >= 15) || month === 10 || month === 11 || (month === 12 && day <= 15);
  return firstPeriod || secondPeriod;
}

/** Weekend: solo il sabato, tranne luglio e agosto. */
function isWeekendCheckIn(date: Date): boolean {
  const month = date.getMonth() + 1;
  return date.getDay() === 6 && month !== 7 && month !== 8;
}

/** Il giorno si può scegliere come check-in per quel pacchetto? */
export function isRomanticCheckInAllowed(pkg: RomanticPackage, date: Date): boolean {
  return pkg === 'week' ? isWeekCheckIn(date) : isWeekendCheckIn(date);
}

/**
 * "2026-10-17": il giorno scelto sul calendario, così com'è. Prima si
 * mandava toISOString(), che è in UTC: la mezzanotte italiana del sabato
 * diventava il venerdì alle 22, e l'email diceva check-in venerdì.
 */
export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** "119 €", "€119": euro senza decimali nel formato della lingua (locale di Intl). */
export function formatWholeEuro(amount: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `€ ${amount}`;
  }
}
